import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Divider,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AppModal } from '../../shared/components/AppModal';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiError } from '../../shared/api/problem';
import { CurrencySelect } from '../../shared/components/CurrencySelect';
import { TimeZoneSelect } from '../../shared/components/TimeZoneSelect';
import { useTenant } from '../tenant/useTenant';
import { useCountries, useStateProvinces } from '../lookups/useLookups';
import {
  createWarehouse,
  saveWarehouseAddress,
  updateWarehouse,
  type WarehouseDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** null/undefined = create; a warehouse = edit. */
  warehouse?: WarehouseDto | null;
  onSaved: () => void;
}

interface FormState {
  warehouseCode: string;
  displayName: string;
  timeZone: string;
  currencyCode: string;
  isActive: boolean;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  countryCode: string;
}

const EMPTY: FormState = {
  warehouseCode: '',
  displayName: '',
  timeZone: 'America/New_York',
  currencyCode: 'USD',
  isActive: true,
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvinceCode: '',
  postalCode: '',
  // Blank, not 'US'. A defaulted country on an otherwise empty form would make
  // every new warehouse look like it has a partial address and meet the refusal
  // below the first time somebody saves one.
  countryCode: '',
};

/** Street, city and country are one thing: all three, or none of them. */
function addressIsPartial(f: FormState): boolean {
  const filled = [f.addressLine1, f.city, f.countryCode].filter((v) => v.trim() !== '').length;
  const extras = [f.addressLine2, f.stateProvinceCode, f.postalCode].some((v) => v.trim() !== '');
  if (filled === 3) return false;
  // A state or a postcode on its own is half an address too, and the server says so.
  return filled > 0 || extras;
}

/**
 * The warehouse saved but its address did not.
 *
 * Its own type so the message can say exactly that. Falling back to the generic "save failed"
 * would send somebody looking for a warehouse that is already there, and re-submitting the form
 * would then be refused for a duplicate code.
 */
class AddressSaveFailed extends Error {}

export function WarehouseFormModal({ opened, onClose, warehouse, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(warehouse);

  // A new warehouse inherits the tenant's defaults (falling back to the
  // platform defaults if the tenant hasn't loaded). Editing keeps the row's
  // own values. Tenant is cached by the shell, so it's loaded well before the
  // modal opens.
  const tenant = useTenant();
  const defaultTimeZone = tenant.data?.defaultTimeZone ?? EMPTY.timeZone;
  const defaultCurrencyCode = tenant.data?.defaultCurrencyCode ?? EMPTY.currencyCode;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const countries = useCountries();
  const states = useStateProvinces(form.countryCode || null);

  const countryOptions = useMemo(
    () => (countries.data ?? []).map((c) => ({ value: c.countryCode, label: c.countryName })),
    [countries.data],
  );

  const stateOptions = useMemo(
    () =>
      (states.data ?? []).map((s) => ({
        value: s.stateProvinceCode,
        label: `${s.stateProvinceCode} — ${s.stateProvinceName}`,
      })),
    [states.data],
  );

  // Reset the form each time the modal opens (create vs the chosen row).
  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setForm(
      warehouse
        ? {
            warehouseCode: warehouse.warehouseCode,
            displayName: warehouse.displayName,
            timeZone: warehouse.timeZone,
            currencyCode: warehouse.currencyCode,
            isActive: warehouse.isActive,
            addressLine1: warehouse.addressLine1 ?? '',
            addressLine2: warehouse.addressLine2 ?? '',
            city: warehouse.city ?? '',
            stateProvinceCode: warehouse.stateProvinceCode ?? '',
            postalCode: warehouse.postalCode ?? '',
            countryCode: warehouse.countryCode ?? '',
          }
        : { ...EMPTY, timeZone: defaultTimeZone, currencyCode: defaultCurrencyCode },
    );
  }, [opened, warehouse, defaultTimeZone, defaultCurrencyCode]);

  const mutation = useMutation({
    // Two calls, because the address is its own endpoint — tenant script 073 keeps it in its own
    // table with its own lifetime rather than folding six columns into usp_Warehouse_Update.
    //
    // The warehouse goes first because a new one has no id until it exists. If the address call
    // then fails the warehouse is still saved, which is why the form refuses a partial address
    // BEFORE submitting and why the failure below says plainly what did and did not happen.
    mutationFn: async () => {
      const saved =
        isEdit && warehouse
          ? await updateWarehouse(warehouse.warehouseId, {
              displayName: form.displayName.trim(),
              timeZone: form.timeZone.trim(),
              currencyCode: form.currencyCode.trim().toUpperCase(),
              isActive: form.isActive,
            })
          : await createWarehouse({
              warehouseCode: form.warehouseCode.trim(),
              displayName: form.displayName.trim(),
              timeZone: form.timeZone.trim() || null,
              currencyCode: form.currencyCode.trim().toUpperCase() || null,
              isActive: form.isActive,
            });

      try {
        return await saveWarehouseAddress(saved.warehouseId, {
          addressLine1: form.addressLine1.trim() || null,
          addressLine2: form.addressLine2.trim() || null,
          city: form.city.trim() || null,
          stateProvinceCode: form.stateProvinceCode.trim() || null,
          postalCode: form.postalCode.trim() || null,
          countryCode: form.countryCode.trim() || null,
        });
      } catch {
        // The warehouse IS saved. Saying otherwise would send somebody looking for a row that is
        // already there, and re-submitting would then fail on a duplicate code.
        throw new AddressSaveFailed();
      }
    },
    onSuccess: () => {
      notifications.show({
        message: isEdit ? t('warehouses.updated') : t('warehouses.created'),
        color: 'green',
      });
      onSaved();
      onClose();
    },
    onError: (err) => {
      if (err instanceof AddressSaveFailed) {
        setSubmitError(t('warehouses.addressSaveFailed'));
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setErrors((e) => ({ ...e, warehouseCode: t('warehouses.errors.codeExists') }));
        return;
      }
      setSubmitError(t('warehouses.errors.saveFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: typeof errors = {};
    if (!isEdit && !form.warehouseCode.trim()) {
      next.warehouseCode = t('warehouses.errors.codeRequired');
    }
    if (!form.displayName.trim()) next.displayName = t('warehouses.errors.nameRequired');
    // Caught here rather than at the server, because the warehouse is saved first and a refusal
    // after that leaves the two halves disagreeing.
    if (addressIsPartial(form)) next.addressLine1 = t('warehouses.addressPartial');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('warehouses.editTitle') : t('warehouses.createTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <TextInput
            label={t('warehouses.code')}
            value={form.warehouseCode}
            onChange={(e) => setForm({ ...form, warehouseCode: e.currentTarget.value })}
            error={errors.warehouseCode}
            disabled={isEdit}
            description={isEdit ? t('warehouses.codeImmutable') : undefined}
            required={!isEdit}
            autoFocus={!isEdit}
          />
          <TextInput
            label={t('warehouses.name')}
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
            error={errors.displayName}
            required
            autoFocus={isEdit}
          />
          <TimeZoneSelect
            label={t('warehouses.timeZone')}
            value={form.timeZone}
            onChange={(v) => setForm({ ...form, timeZone: v })}
          />
          <CurrencySelect
            label={t('warehouses.currency')}
            value={form.currencyCode}
            onChange={(v) => setForm({ ...form, currencyCode: v })}
          />
          <Switch
            label={t('warehouses.active')}
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
          />

          <Divider
            label={
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {t('warehouses.addressSection')}
                </Text>
              </Stack>
            }
            labelPosition="left"
          />
          <Text size="xs" c="dimmed" mt={-8}>
            {t('warehouses.addressHelp')}
          </Text>

          <TextInput
            label={t('warehouses.address1')}
            value={form.addressLine1}
            onChange={(e) => setForm({ ...form, addressLine1: e.currentTarget.value })}
            error={errors.addressLine1}
            maxLength={255}
          />
          <TextInput
            label={t('warehouses.address2')}
            value={form.addressLine2}
            onChange={(e) => setForm({ ...form, addressLine2: e.currentTarget.value })}
            maxLength={255}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('warehouses.city')}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.currentTarget.value })}
              maxLength={100}
            />
            <Select
              label={t('warehouses.country')}
              data={countryOptions}
              value={form.countryCode || null}
              // Changing the country clears the state, exactly as the ship-to form does: the codes
              // are per country, so keeping the old one submits a subdivision from somewhere else.
              onChange={(v) => setForm({ ...form, countryCode: v ?? '', stateProvinceCode: '' })}
              disabled={countries.isLoading}
              searchable
              clearable
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label={t('warehouses.stateProvince')}
              placeholder={t('warehouses.statePlaceholder')}
              description={
                stateOptions.length === 0 && !states.isLoading
                  ? t('warehouses.noStates')
                  : undefined
              }
              data={stateOptions}
              value={form.stateProvinceCode || null}
              onChange={(v) => setForm({ ...form, stateProvinceCode: v ?? '' })}
              disabled={states.isLoading || stateOptions.length === 0}
              searchable
              clearable
            />
            <TextInput
              label={t('warehouses.postalCode')}
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.currentTarget.value })}
              maxLength={20}
            />
          </SimpleGrid>

          {submitError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {isEdit ? t('shell.saveChanges') : t('warehouses.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
